/**
 * How a route dialog's `onSubmit` reports a failed save.
 *
 * The library dialog (@intrasync/react 31, `Dialog.Footer`) awaits `onSubmit`:
 * when it RESOLVES the dialog closes, and when it REJECTS the dialog stays open
 * and shows the rejection's message in an error alert at its foot. A view
 * store's save returns `false` and records why on the store, so an `onSubmit`
 * that just awaits the save closes the dialog on a refused save and the reason
 * is never seen. Pass the save's result and the store's reason here instead:
 *
 * ```ts
 * onSubmit: async ({ rootStore }) => {
 *   const store = storeOf(rootStore);
 *   throwIfFailed(await store.save(), store.formError, "The partner could not be saved.");
 * }
 * ```
 *
 * The reason is read after the save settles (arguments evaluate left to
 * right), and `fallback` covers a store that refused without saying why. The
 * footer is where the reason shows, so the dialog body should not render the
 * same field again.
 */
export function throwIfFailed(
  ok: boolean | null | undefined,
  reason: string | null | undefined,
  fallback: string,
): void {
  if (!ok) throw new Error(reason || fallback);
}

/**
 * Per-call ServiceStore params that make a refused create/patch/remove REJECT.
 *
 * HQ's ServiceStores run with the library default `throwOnError: false`: a
 * refused write RESOLVES null and records the reason on `errorOn*`, so a view
 * store that wraps the call in try/catch reports the refusal as a success. Pass
 * this as the call's params where the caller catches (it is never sent over
 * the wire). Per call rather than store-wide, because most of these stores have
 * other readers that treat a null result as the failure.
 */
export const THROW_ON_ERROR = { throwOnError: true } as never;
